import type { ReactElement } from 'react';

export type GradeDistribution = Record<string, number>;

export interface GradeDistributionChartProps {
  distribution: GradeDistribution | null | undefined;
  totalStudents?: number;
  averageGpa?: number;
  variant?: 'compact' | 'full';
}

interface GradeConfig {
  key: string;
  label: string;
  color: string;
  textColor: string;
}

/**
 * GradeDistributionChart - Displays grade distribution as a horizontal stacked bar chart.
 */
const GradeDistributionChart = ({
  distribution,
  totalStudents,
  averageGpa,
  variant = 'compact',
}: GradeDistributionChartProps): ReactElement | null => {
  if (!distribution) return null;

  const grades: GradeConfig[] = [
    { key: 'A', label: 'A', color: 'bg-green-500', textColor: 'text-green-700' },
    { key: 'B', label: 'B', color: 'bg-yellow-400', textColor: 'text-yellow-700' },
    { key: 'C', label: 'C', color: 'bg-orange-400', textColor: 'text-orange-700' },
    { key: 'D', label: 'D', color: 'bg-orange-600', textColor: 'text-orange-800' },
    { key: 'F', label: 'F', color: 'bg-red-500', textColor: 'text-red-700' },
    { key: 'W', label: 'W', color: 'bg-gray-400', textColor: 'text-gray-600' },
  ];

  const activeGrades = grades.filter((g) => (distribution[g.key] ?? 0) > 0);

  if (variant === 'compact') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span className="font-medium">Grade Distribution</span>
          <span>
            {averageGpa != null && `GPA ${averageGpa.toFixed(2)} · `}
            {totalStudents?.toLocaleString()} students
          </span>
        </div>

        <div
          className="h-4 flex rounded-md overflow-hidden"
          role="img"
          aria-label="Grade distribution chart"
        >
          {activeGrades.map((grade) => {
            const percentage = distribution[grade.key];
            if (percentage <= 0) return null;

            return (
              <div
                key={grade.key}
                className={`${grade.color} relative group transition-opacity hover:opacity-90`}
                style={{ width: `${percentage}%` }}
                title={`${grade.label}: ${percentage.toFixed(1)}%`}
              >
                {percentage >= 8 && (
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                    {grade.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {activeGrades.map((grade) => {
            const percentage = distribution[grade.key];
            if (percentage <= 0) return null;

            return (
              <div key={grade.key} className="flex items-center gap-1">
                <div className={`w-2.5 h-2.5 rounded-sm ${grade.color}`} />
                <span className={grade.textColor}>
                  {grade.label}: {percentage.toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Grade Distribution</span>
        <div className="text-sm text-gray-600">
          {averageGpa != null && (
            <span className="font-medium">GPA {averageGpa.toFixed(2)}</span>
          )}
          {totalStudents && (
            <span className="ml-2 text-gray-500">
              ({totalStudents.toLocaleString()} students)
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        {grades.map((grade) => {
          const percentage = distribution[grade.key] || 0;

          return (
            <div key={grade.key} className="flex items-center gap-2">
              <span className={`w-4 text-xs font-medium ${grade.textColor}`}>{grade.label}</span>
              <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                <div
                  className={`h-full ${grade.color} transition-all duration-300`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="w-12 text-xs text-gray-600 text-right">
                {percentage.toFixed(1)}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GradeDistributionChart;
